import { useFrontendTool } from "@copilotkit/react-core/v2";
import { z } from "zod";
import { executeTql, getHealthData, getCheckpointData } from "../utils/tungsten-api";
import { buildMonSummary, buildMonComponentSummary } from "../utils/mon-summary";

/**
 * Frontend ports of backend Striim server API tools (striim-ai repo
 * `agent_langgraph/agents/striim_ai/components/shared_tools/` and
 * `components/sub-agents/striim_diagnostics/tools/`).
 *
 * Each tool keeps the same name and behaviour as its backend counterpart but talks to the Striim
 * server from the browser (see `../utils/tungsten-api`) instead of executing TQL/REST calls on the
 * backend. This lets the tools work against a user's Striim cluster even when the AI server is
 * centrally hosted and cannot reach it directly.
 *
 * The backend `auth_token` parameter is intentionally dropped — the browser always uses the
 * authenticated session token.
 */

const VALID_LIFECYCLE_ACTIONS = ["DEPLOY", "START", "STOP", "UNDEPLOY"] as const;
const VALID_RESOURCE_TYPES = ["SOURCES", "TARGETS", "STREAMS", "CQS", "TYPES", "CACHES"] as const;

const COMPONENT_HEALTH_MAPS: Array<{
    type: string;
    key: string;
    metrics: (data: Record<string, unknown>) => string;
}> = [
    {
        type: "Application",
        key: "appHealthMap",
        metrics: d => `status=${d.status ?? "N/A"}, rate=${d.rate ?? "N/A"}`
    },
    {
        type: "Source",
        key: "sourceHealthMap",
        metrics: d => `eventRate=${d.eventRate ?? "N/A"}, lastEventTime=${d.lastEventTime ?? "N/A"}`
    },
    {
        type: "Target",
        key: "targetHealthMap",
        metrics: d =>
            `eventRate=${d.eventRate ?? "N/A"}, lagEnd2End=${d.lagEnd2End ?? "N/A"}, lastWriteTime=${d.lastWriteTime ??
                "N/A"}`
    },
    {
        type: "Server",
        key: "serverHealthMap",
        metrics: d => `cpu=${d.cpu ?? "N/A"}, diskFree=${d.diskFree ?? "N/A"}`
    },
    { type: "WA Store", key: "waStoreHealthMap", metrics: () => "" },
    { type: "Cache", key: "cacheHealthMap", metrics: () => "" }
];

/** Build the markdown component list from a raw /health response (ports the backend logic). */
function buildHealthMarkdown(healthData: unknown): string | { error: string; error_type: string } {
    if (typeof healthData !== "object" || healthData === null) {
        return { error: "Invalid health API response structure", error_type: "APIError" };
    }

    const data = healthData as Record<string, unknown>;
    if ("error" in data) {
        return data as { error: string; error_type: string };
    }

    const healthRecords = data.healthRecords;
    if (!Array.isArray(healthRecords)) {
        return { error: "Invalid health API response structure", error_type: "APIError" };
    }
    if (healthRecords.length === 0) {
        return "No user components found in the cluster.";
    }

    const record = healthRecords[0] as Record<string, unknown>;
    const lines: string[] = [];

    for (const { type, key, metrics } of COMPONENT_HEALTH_MAPS) {
        const map = record[key];
        if (typeof map !== "object" || map === null) {
            continue;
        }
        const entries = Object.entries(map as Record<string, Record<string, unknown>>)
            .filter(([id]) => !id.includes("System$"))
            .sort(([a], [b]) => a.localeCompare(b));

        for (const [id, componentData] of entries) {
            const metricStr = metrics(componentData ?? {});
            lines.push(metricStr ? `${type} - ${id} (${metricStr})` : `${type} - ${id}`);
        }
    }

    return lines.length > 0 ? lines.join("\n") : "No user components found in the cluster.";
}

export const useTungstenTools = () => {
    useFrontendTool({
        name: "application_lifecycle_tool",
        description:
            "Manage Striim application lifecycle: DEPLOY, START, STOP, UNDEPLOY. " +
            "Executes TQL: `DEPLOY APPLICATION|START|STOP|UNDEPLOY APPLICATION <namespace>.<app>;`",
        parameters: z.object({
            action: z.enum(VALID_LIFECYCLE_ACTIONS).describe("One of: DEPLOY, START, STOP, UNDEPLOY"),
            app_name: z.string().describe("Fully qualified app name (e.g., 'admin.MyApp')")
        }),
        handler: async ({ action, app_name }) => {
            const normalizedAction = action.trim().toUpperCase();
            const appName = app_name.trim();
            if (!appName.includes(".")) {
                return {
                    error: `Invalid app_name '${appName}'. Must be in NAMESPACE.APPNAME format (e.g., 'admin.MyApp')`,
                    error_type: "ValidationError"
                };
            }

            const command =
                normalizedAction === "DEPLOY" || normalizedAction === "UNDEPLOY"
                    ? `${normalizedAction} APPLICATION ${appName};`
                    : `${normalizedAction} ${appName};`;
            return executeTql(command);
        }
    });

    useFrontendTool({
        name: "application_status_tool",
        description:
            "Get detailed status of a Striim application. Returns application state, component " +
            "states/rates, timestamps, and errors. Executes TQL: `STATUS <namespace>.<app>;`",
        parameters: z.object({
            app_name: z.string().describe("Fully qualified app name (e.g., 'admin.MyApp')")
        }),
        handler: async ({ app_name }) => {
            const appName = app_name.trim();
            if (!appName.includes(".")) {
                return {
                    error: `Invalid app_name '${appName}'. Must be in NAMESPACE.APPNAME format (e.g., 'admin.MyApp')`,
                    error_type: "ValidationError"
                };
            }
            return executeTql(`STATUS ${appName};`);
        }
    });

    useFrontendTool({
        name: "component_inspection_tool",
        description:
            "Describe a Striim component's configuration and structure. Returns properties, settings, " +
            "relationships, and schema info. Executes TQL: `DESCRIBE [APPLICATION] <namespace>.<component>;`",
        parameters: z.object({
            component_name: z
                .string()
                .describe("Fully qualified component name (e.g., 'admin.MyApp' or 'admin.MyApp_KafkaSource')"),
            is_application: z
                .boolean()
                .optional()
                .default(false)
                .describe("True if describing an APPLICATION, False for all other component types.")
        }),
        handler: async ({ component_name, is_application }) => {
            const componentName = component_name.trim();
            if (!componentName.includes(".")) {
                return {
                    error: `Invalid component_name '${componentName}'. Must be in NAMESPACE.COMPONENT_NAME format (e.g., 'admin.MyApp')`,
                    error_type: "ValidationError"
                };
            }
            const command = is_application ? `DESCRIBE APPLICATION ${componentName};` : `DESCRIBE ${componentName};`;
            return executeTql(command);
        }
    });

    useFrontendTool({
        name: "component_monitoring_tool",
        description:
            "Monitor Striim components using the MON command. No args: `MON;` lists all applications " +
            "with states and metrics. With component: `MON <component>;` returns detailed real-time " +
            "metrics for that component. Use this (with no arguments) to list applications. " +
            "ALWAYS use this tool — never component_status_tool, never list_components_from_health_api — " +
            "whenever the user's request is or starts with the literal word 'mon' (e.g. 'mon', " +
            "'mon admin.MyApp', 'mon csvdatasource') or otherwise says 'monitor'/'monitoring'. Pass the " +
            "component name through exactly as given, even if it is a bare/unqualified name with no " +
            "'NAMESPACE.' prefix — do NOT look up or resolve the full identifier first. " +
            "The result carries its metrics under an `output` property (a single node for one " +
            "component, or a recursive tree of nodes for a component with children). When presenting " +
            "the result to the user, every stat found under `output` (and, recursively, under each " +
            "child component) MUST be displayed — do not omit or cherry-pick fields. Render the stats " +
            "as a well-formatted markdown table, one row per stat (or one row per component with a " +
            "column per stat when there are multiple components), not as prose.",
        parameters: z.object({
            component: z
                .string()
                .optional()
                .describe("Component to monitor (e.g., 'admin.MyApp'). Omit to list all applications.")
        }),
        handler: async ({ component }) => {
            if (component) {
                const rawResponse = await executeTql(`MON ${component.trim().replace(/;+$/, "")};`);
                return rawResponse;
            }
            const rawResponse = await executeTql("MON;");
            return buildMonSummary(rawResponse);
        }
    });

    useFrontendTool({
        name: "component_status_tool",
        description:
            "Get detailed status of a single Striim component (source, target, stream, CQ). Returns " +
            "state, processing rate, timestamps, and errors for one component. Use application_status_tool " +
            "instead for whole-application status. Executes TQL: `STATUS <namespace>.<component>;`. " +
            "Do NOT use this for 'mon'/'monitor' requests, even for a single component — those must " +
            "always use component_monitoring_tool instead. Only use this tool when the user's request " +
            "explicitly asks for 'status', not 'mon'.",
        parameters: z.object({
            component_name: z.string().describe("Fully qualified component name (e.g., 'admin.MyApp_KafkaSource')")
        }),
        handler: async ({ component_name }) => {
            const componentName = component_name.trim();
            if (!componentName.includes(".")) {
                return {
                    error: `Invalid component_name '${componentName}'. Must be in NAMESPACE.COMPONENT_NAME format (e.g., 'admin.MySource')`,
                    error_type: "ValidationError"
                };
            }
            return executeTql(`STATUS ${componentName};`);
        }
    });

    useFrontendTool({
        name: "resource_listing_tool",
        description:
            "List Striim resources by type. Executes TQL: `LIST <resource_type>;`. " +
            "To list applications, use component_monitoring_tool() instead.",
        parameters: z.object({
            resource_type: z
                .enum(VALID_RESOURCE_TYPES)
                .describe(
                    "One of: SOURCES, TARGETS, STREAMS, CQS, TYPES, CACHES. For applications, use component_monitoring_tool() instead."
                )
        }),
        handler: async ({ resource_type }) => {
            return executeTql(`LIST ${resource_type.trim().toUpperCase()};`);
        }
    });

    useFrontendTool({
        name: "get_application_checkpoint_data",
        description:
            "Retrieve checkpoint/recovery position data for a Striim application, useful for " +
            "diagnosing replication lag and data consistency.",
        parameters: z.object({
            app_name: z.string().describe("Fully qualified app name (e.g., 'admin.MyApp')")
        }),
        handler: async ({ app_name }) => {
            const appName = app_name.trim();
            if (!appName.includes(".")) {
                return {
                    error: `Invalid app_name '${appName}'. Must be in NAMESPACE.APPNAME format (e.g., 'admin.MyApp')`,
                    error_type: "ValidationError"
                };
            }
            return getCheckpointData(appName);
        }
    });

    useFrontendTool({
        name: "list_components_from_health_api",
        description:
            "List all component IDs from the Striim health API in markdown format with key metrics. " +
            "Returns all non-system components in the cluster organized by type (Application, Source, " +
            "Target, Server, WA Store, Cache) with relevant metrics. Use this for a comprehensive list " +
            "of components and to resolve full NAMESPACE.NAME identifiers. Do NOT use this for 'mon'/" +
            "'monitor' requests, even when the component name given is unqualified (no 'NAMESPACE.' " +
            "prefix) — those must always use component_monitoring_tool directly, passing the name as-is.",
        parameters: z.object({}),
        handler: async () => {
            const healthData = await getHealthData();
            return buildHealthMarkdown(healthData);
        }
    });
};
